/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useMemo, useState } from "react";

interface Props {
  pin: string;
  onUnlock: () => void;
}

/**
 * Functional 4-function calculator that hides an SOS-in-progress state.
 * Appending the configured PIN (default `911=`) during data entry unlocks
 * the real SOS screen. A tiny 2-px dot in the corner pulses to confirm SOS
 * is running silently in the background.
 */
export default function StealthCalculator({ pin, onUnlock }: Props) {
  const [buffer, setBuffer] = useState("0");
  const [prev, setPrev] = useState<number | null>(null);
  const [op, setOp] = useState<"+" | "-" | "*" | "/" | null>(null);
  const [justEvaluated, setJustEvaluated] = useState(false);

  const keys = useMemo(
    () =>
      [
        ["C", "±", "%", "÷"],
        ["7", "8", "9", "×"],
        ["4", "5", "6", "−"],
        ["1", "2", "3", "+"],
        ["0", ".", "="],
      ] as const,
    [],
  );

  const maybeUnlock = (candidate: string) => {
    if (candidate.endsWith(pin)) {
      onUnlock();
      return true;
    }
    return false;
  };

  const appendDigit = (d: string) => {
    const next = justEvaluated || buffer === "0" ? d : buffer + d;
    if (maybeUnlock(next)) return;
    setBuffer(next);
    setJustEvaluated(false);
  };

  const appendDot = () => {
    if (buffer.includes(".")) return;
    setBuffer(buffer + ".");
    setJustEvaluated(false);
  };

  const doEvaluate = (): number => {
    const cur = Number(buffer);
    if (prev == null || op == null) return cur;
    switch (op) {
      case "+":
        return prev + cur;
      case "-":
        return prev - cur;
      case "*":
        return prev * cur;
      case "/":
        return cur === 0 ? NaN : prev / cur;
    }
  };

  const handleOp = (o: "+" | "-" | "*" | "/") => {
    const cur = Number(buffer);
    if (prev != null && op != null && !justEvaluated) {
      const result = doEvaluate();
      setPrev(result);
      setBuffer(format(result));
    } else {
      setPrev(cur);
    }
    setOp(o);
    setJustEvaluated(true);
  };

  const handleEquals = () => {
    const result = doEvaluate();
    // PIN can be typed across digits followed by "=" — build the candidate
    // from the currently displayed buffer plus "=" and test.
    if (maybeUnlock(buffer + "=")) return;
    setBuffer(format(result));
    setPrev(null);
    setOp(null);
    setJustEvaluated(true);
  };

  const handleClear = () => {
    setBuffer("0");
    setPrev(null);
    setOp(null);
    setJustEvaluated(false);
  };

  const handleSign = () => {
    if (buffer === "0") return;
    setBuffer(buffer.startsWith("-") ? buffer.slice(1) : "-" + buffer);
  };

  const handlePercent = () => {
    setBuffer(format(Number(buffer) / 100));
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1c1c1e] flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Hidden SOS-active indicator: a nearly-invisible 2px dot that pulses. */}
      <div className="absolute top-2 right-2">
        <span
          className="block w-[3px] h-[3px] rounded-full bg-red-500 animate-pulse"
          aria-hidden
        />
      </div>

      <div className="flex-1 flex items-end justify-end px-6 pb-4">
        <span className="text-white text-6xl font-light tabular-nums truncate">
          {buffer}
        </span>
      </div>

      <div className="px-3 pb-6 grid gap-2">
        {keys.map((row, rIdx) => (
          <div
            key={rIdx}
            className={`grid gap-2 ${
              rIdx === 4 ? "grid-cols-[2fr_1fr_1fr_1fr]" : "grid-cols-4"
            }`}
          >
            {rIdx === 4
              ? [
                  <button
                    key="0"
                    onClick={() => appendDigit("0")}
                    className="h-16 rounded-full bg-[#333] text-white text-2xl col-span-2 text-left pl-8"
                  >
                    0
                  </button>,
                  <button
                    key="."
                    onClick={appendDot}
                    className="h-16 rounded-full bg-[#333] text-white text-2xl"
                  >
                    .
                  </button>,
                  <button
                    key="="
                    onClick={handleEquals}
                    className="h-16 rounded-full bg-orange-500 text-white text-2xl"
                  >
                    =
                  </button>,
                ]
              : row.map((k) => (
                  <button
                    key={k}
                    onClick={() => {
                      if (k === "C") handleClear();
                      else if (k === "±") handleSign();
                      else if (k === "%") handlePercent();
                      else if (k === "÷") handleOp("/");
                      else if (k === "×") handleOp("*");
                      else if (k === "−") handleOp("-");
                      else if (k === "+") handleOp("+");
                      else appendDigit(k);
                    }}
                    className={`h-16 rounded-full text-2xl ${
                      k === "C" || k === "±" || k === "%"
                        ? "bg-[#a5a5a5] text-black"
                        : k === "÷" || k === "×" || k === "−" || k === "+"
                          ? "bg-orange-500 text-white"
                          : "bg-[#333] text-white"
                    }`}
                  >
                    {k}
                  </button>
                ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function format(n: number): string {
  if (!Number.isFinite(n)) return "Error";
  const s = String(n);
  return s.length > 12 ? n.toPrecision(10) : s;
}
